<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-amber-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <section class="fac-hero w-full rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div class="min-w-0 flex-1">
                            <p class="fac-hero__eyebrow">Campus Facilities</p>
                            <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                                {{ campusTitle }} Facility
                            </h1>
                            <p class="mt-2 text-gray-600 max-w-2xl leading-relaxed">
                                Building profile, rooms, and campus facility inventory. Start with Building Overview;
                                more sections will follow from the campus details workbook.
                            </p>
                        </div>
                        <div class="fac-hero__meta shrink-0">
                            <div class="fac-hero__badge">
                                <span class="fac-hero__badge-label">Entity</span>
                                <span class="fac-hero__badge-value">{{ entity }} · {{ campusCode }}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <div v-if="pageLoading" class="flex justify-center py-10">
                    <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                </div>

                <template v-else>
                    <section class="mb-8">
                        <div class="flex items-end justify-between gap-3 mb-4">
                            <div>
                                <p class="fac-charts__eyebrow">Campus footprint</p>
                                <h2 class="fac-charts__heading">Infrastructure areas</h2>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <div
                                v-for="(area, index) in facilityAreaItems"
                                :key="'area-' + index"
                                class="fac-area-tile"
                                :class="[
                                    'fac-area-tile--' + (area.accent || 'amber'),
                                    { 'fac-area-tile--empty': area.value === null },
                                ]"
                            >
                                <div class="fac-area-tile__icon" aria-hidden="true">
                                    <font-awesome-icon :icon="['fas', area.icon || 'building']" />
                                </div>
                                <div class="fac-area-tile__copy min-w-0">
                                    <p class="fac-area-tile__label">{{ area.title }}</p>
                                    <p class="fac-area-tile__value">
                                        {{ formatAreaValue(area.value) }}
                                        <span
                                            v-if="area.value !== null"
                                            class="fac-area-tile__unit"
                                        >{{ area.unit || "m²" }}</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <FacilityAreasChart :data="facilityAreaItems" />
                    </section>

                    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                        <div
                            v-for="kpi in kpiCards"
                            :key="kpi.key"
                            class="fac-kpi"
                            :class="{ 'fac-kpi--tip': !!kpi.tip }"
                            :tabindex="kpi.tip ? 0 : null"
                        >
                            <div class="fac-kpi__label-row">
                                <div class="fac-kpi__label">{{ kpi.label }}</div>
                                <span v-if="kpi.tip" class="fac-kpi__hint" aria-hidden="true">
                                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                                </span>
                            </div>
                            <div class="fac-kpi__value">{{ kpi.value }}</div>
                            <div v-if="kpi.tip" class="fac-kpi__tooltip" role="tooltip">
                                <p class="fac-kpi__tooltip-title">How it's calculated</p>
                                <p class="fac-kpi__tooltip-text">{{ kpi.tip }}</p>
                            </div>
                        </div>
                    </div>

                    <section class="mb-8">
                        <div class="flex items-end justify-between gap-3 mb-4">
                            <div>
                                <p class="fac-charts__eyebrow">Visual overview</p>
                                <h2 class="fac-charts__heading">Facility analytics</h2>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                            <FacilityCategoryMixChart :data="categoryMixChartData" />
                            <FacilityTopSectionsChart
                                :data="topSectionsChartData"
                                @select="onTopSectionSelect"
                            />
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <FacilitySeatingCapacityChart
                                :data="seatingCapacityChartData"
                                @select="onTopSectionSelect"
                            />
                            <FacilityOfficeSplitChart
                                :academic="summary.academic_offices"
                                :admin="summary.admin_offices"
                                @select="onTopSectionSelect"
                            />
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                            <FacilityPalmTreesChart
                                :data="summary.palm_trees_by_type"
                                @type-select="onPalmTreesTypeSelect"
                            />
                            <FacilitySecurityCamerasChart :data="summary.security_cameras_by_area" />
                        </div>
                    </section>

                    <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                            <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                                <font-awesome-icon :icon="['fas', 'building']" class="text-amber-600 mr-2" />
                                Facility Sections
                            </h2>
                            <div class="flex items-center gap-3">
                                <span class="text-xs text-gray-500">
                                    {{ enabledModuleCount }} active
                                </span>
                                <button
                                    type="button"
                                    class="fac-export-btn"
                                    :disabled="pageLoading"
                                    @click="exportAllExcel"
                                >
                                    <font-awesome-icon
                                        :icon="['fas', 'file-excel']"
                                        class="mr-2"
                                    />
                                    <span>Export Excel</span>
                                </button>
                            </div>
                        </div>

                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            <button v-for="module in modules" :key="module.key" type="button" class="fac-module" :class="{
                                'fac-module--active': module.enabled,
                                'fac-module--soon': !module.enabled,
                            }" :disabled="!module.enabled" @click="openModule(module)">
                                <div class="fac-module__icon">{{ module.icon }}</div>
                                <div class="fac-module__label">{{ module.label }}</div>
                                <div v-if="module.enabled && module.count !== null" class="fac-module__count">
                                    {{ module.count }} {{ pluralize(module.unit, module.count) }}
                                </div>
                                <div v-else-if="!module.enabled" class="fac-module__soon">Coming soon</div>
                            </button>
                        </div>
                    </section>
                </template>
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="activeModule" :title="activeModule.label" @close="closeModule">
                <component
                    :is="activeModuleComponent"
                    :key="activeModuleKey"
                    :entity="entity"
                    :campus="campusCode"
                    v-bind="activeModuleExtraProps"
                    @changed="onModuleChanged"
                />
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import gql from "graphql-tag";
import BreadCrumb from "../../../components/shared/BreadCrumb.vue";
import FacilityCategoryMixChart from "./charts/FacilityCategoryMixChart.vue";
import FacilityTopSectionsChart from "./charts/FacilityTopSectionsChart.vue";
import FacilitySeatingCapacityChart from "./charts/FacilitySeatingCapacityChart.vue";
import FacilityOfficeSplitChart from "./charts/FacilityOfficeSplitChart.vue";
import FacilityPalmTreesChart from "./charts/FacilityPalmTreesChart.vue";
import FacilitySecurityCamerasChart from "./charts/FacilitySecurityCamerasChart.vue";
import FacilityAreasChart from "./charts/FacilityAreasChart.vue";
import { exportAllFacilitySections, getTotalSheetCount } from "./facilityExport.js";
import {
    ADPOLY_ENTITY,
    getCampusConfig,
    isValidCampus,
} from "../config";

var FACILITY_AREA_FIELDS = [
    {
        key: "landscape_area",
        title: "Landscape Area",
        icon: "leaf",
        accent: "teal",
    },
    {
        key: "main_building",
        title: "Main Building",
        icon: "building",
        accent: "amber",
    },
    {
        key: "gym_building",
        title: "Gym Building",
        icon: "dumbbell",
        accent: "orange",
    },
    {
        key: "students_center_building",
        title: "Students Center Building",
        icon: "users",
        accent: "stone",
    },
];

const SUMMARY_QUERY = gql`
    query FacilitySummary($entity: String, $campus: String) {
        facilityArea(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                id
                landscape_area
                main_building
                gym_building
                students_center_building
                unit
            }
        }
        facilityAreasSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityBuildingOverviewsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                floors_count
            }
        }
        facilityClassroomsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                total_seats
            }
        }
        facilityLabsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                total_seats
            }
        }
        facilityAcademicOfficesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                occupied_count
                stations_count
            }
        }
        facilityAdminOfficesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                occupied_count
                stations_count
            }
        }
        facilityAuditoriumsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                total_capacity
            }
        }
        facilityMeetingRoomsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                total_capacity
            }
        }
        facilityParkingAreasSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityParkingTransportServicesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityParkingHelipadsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityGatesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityToiletsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityStoresSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityServiceRoomsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilitySportFacilitiesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityCanteensSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityClinicsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityPrayerRoomsSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityPantriesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityVendingMachinesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
            }
        }
        facilityPalmTreesSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                total_plants
                by_plant_type {
                    label
                    value
                }
            }
        }
        facilitySecurityCamerasSummary(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                total
                total_cameras
                by_area {
                    label
                    value
                }
            }
        }
    }
`;

var moduleComponentCache = {};

function resolveModuleComponent(key) {
    if (!key) {
        return null;
    }
    if (!moduleComponentCache[key]) {
        moduleComponentCache[key] = defineAsyncComponent(function () {
            return import("./modules/" + key + "Module.vue");
        });
    }
    return moduleComponentCache[key];
}

export default {
    name: "adpoly-facility",
    components: {
        BreadCrumb,
        FacilityCategoryMixChart,
        FacilityTopSectionsChart,
        FacilitySeatingCapacityChart,
        FacilityOfficeSplitChart,
        FacilityPalmTreesChart,
        FacilitySecurityCamerasChart,
        FacilityAreasChart,
    },
    props: ["campus"],
    data() {
        return {
            entity: ADPOLY_ENTITY,
            summaryLoading: false,
            facilityArea: null,
            summary: {
                floors_count: 0,
                classrooms: 0,
                labs: 0,
                offices: 0,
                academic_offices: 0,
                admin_offices: 0,
                seating_capacity: 0,
                classroom_seats: 0,
                lab_seats: 0,
                auditorium_capacity: 0,
                meeting_room_capacity: 0,
                parking: 0,
                toilets: 0,
                prayer_rooms: 0,
                palm_trees_by_type: [],
                security_cameras_by_area: [],
            },
            activeModule: null,
            activePlantTypeFilter: "",
            modules: [
                {
                    key: "BuildingOverview",
                    label: "Building Overview",
                    icon: "🏢",
                    unit: "profile",
                    enabled: true,
                    count: null,
                },
                {
                    key: "FacilityAreas",
                    label: "Campus Areas",
                    icon: "📐",
                    unit: "record",
                    enabled: true,
                    count: null,
                },
                { key: "Classrooms", label: "Classrooms", icon: "🏫", unit: "room", enabled: true, count: null },
                { key: "Labs", label: "Labs", icon: "🔬", unit: "lab", enabled: true, count: null },
                { key: "AcademicOffices", label: "Academic Offices", icon: "🗂️", unit: "office", enabled: true, count: null },
                { key: "AdminOffices", label: "Admin Offices", icon: "🏛️", unit: "office", enabled: true, count: null },
                { key: "Auditorium", label: "Auditorium", icon: "🎭", unit: "hall", enabled: true, count: null },
                { key: "MeetingRooms", label: "Meeting Rooms", icon: "👥", unit: "room", enabled: true, count: null },
                { key: "CarParking", label: "Car Parking", icon: "🅿️", unit: "record", enabled: true, count: null },
                { key: "Gates", label: "Gates", icon: "🚪", unit: "gate", enabled: true, count: null },
                { key: "Toilets", label: "Toilets", icon: "🚻", unit: "toilet", enabled: true, count: null },
                { key: "Stores", label: "Stores", icon: "📦", unit: "store", enabled: true, count: null },
                { key: "ServiceRooms", label: "Service Rooms", icon: "🧰", unit: "room", enabled: true, count: null },
                { key: "SportFacilities", label: "Sport Facilities", icon: "⚽", unit: "facility", enabled: true, count: null },
                { key: "Canteen", label: "Canteen", icon: "🍽️", unit: "area", enabled: true, count: null },
                { key: "Clinic", label: "Clinic", icon: "🏥", unit: "clinic", enabled: true, count: null },
                { key: "PrayerRoom", label: "Prayer Room", icon: "🕌", unit: "room", enabled: true, count: null },
                { key: "PantriesKitchen", label: "Pantries & Kitchen", icon: "🍳", unit: "area", enabled: true, count: null },
                { key: "VendingMachines", label: "Vending Machines", icon: "🥤", unit: "machine", enabled: true, count: null },
                { key: "PalmAndTrees", label: "Palm & Trees", icon: "🌴", unit: "item", enabled: true, count: null },
                { key: "SecurityCam", label: "Security Camera", icon: "📷", unit: "area", enabled: true, count: null },
            ],
        };
    },
    created() {
        this.validateCampus();
        this.loadSummary();
    },
    watch: {
        campus: function () {
            this.validateCampus();
            this.loadSummary();
        },
    },
    computed: {
        pageLoading() {
            return this.summaryLoading;
        },
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        facilityAreaItems() {
            var record = this.facilityArea;
            var unit = record && record.unit ? record.unit : "m²";
            var items = [];
            for (var i = 0; i < FACILITY_AREA_FIELDS.length; i++) {
                var field = FACILITY_AREA_FIELDS[i];
                var raw = record ? record[field.key] : null;
                var value =
                    raw === null || raw === undefined || raw === ""
                        ? null
                        : Number(raw);
                if (value !== null && isNaN(value)) {
                    value = null;
                }
                items.push({
                    key: field.key,
                    title: field.title,
                    value: value,
                    unit: unit,
                    icon: field.icon,
                    accent: field.accent,
                });
            }
            return items;
        },
        enabledModuleCount() {
            return this.modules.filter(function (m) {
                return m.enabled;
            }).length;
        },
        kpiCards() {
            var s = this.summary;
            return [
                {
                    key: "floors",
                    label: "Floors",
                    value: s.floors_count,
                    tip: null,
                },
                {
                    key: "classrooms",
                    label: "Classrooms",
                    value: s.classrooms,
                    tip: null,
                },
                {
                    key: "labs",
                    label: "Labs",
                    value: s.labs,
                    tip: null,
                },
                {
                    key: "offices",
                    label: "Offices",
                    value: s.offices,
                    tip: "Academic offices + Admin offices",
                },
                {
                    key: "seating_capacity",
                    label: "Seating Capacity",
                    value: s.seating_capacity,
                    tip: "Classroom seats + Lab seats + Auditorium capacity + Meeting room capacity",
                },
                {
                    key: "parking",
                    label: "Parking Areas",
                    value: s.parking,
                    tip: "Count of parking areas only",
                },
                {
                    key: "toilets",
                    label: "Toilets",
                    value: s.toilets,
                    tip: null,
                },
                {
                    key: "prayer_rooms",
                    label: "Prayer Rooms",
                    value: s.prayer_rooms,
                    tip: null,
                },
            ];
        },
        categoryMixChartData() {
            var modules = this.modules;
            function sectionsOf(keys) {
                var sections = [];
                var total = 0;
                for (var i = 0; i < keys.length; i++) {
                    var key = keys[i];
                    for (var j = 0; j < modules.length; j++) {
                        if (modules[j].key === key) {
                            var count =
                                modules[j].count !== null ? modules[j].count || 0 : 0;
                            sections.push({
                                label: modules[j].label,
                                count: count,
                            });
                            total += count;
                            break;
                        }
                    }
                }
                return { sections: sections, value: total };
            }

            var teaching = sectionsOf([
                "Classrooms",
                "Labs",
                "Auditorium",
                "MeetingRooms",
            ]);
            var offices = sectionsOf(["AcademicOffices", "AdminOffices"]);
            var support = sectionsOf(["Gates", "Toilets", "Stores", "ServiceRooms"]);
            var amenities = sectionsOf([
                "SportFacilities",
                "Canteen",
                "Clinic",
                "PrayerRoom",
                "PantriesKitchen",
                "VendingMachines",
            ]);
            var grounds = sectionsOf(["CarParking", "PalmAndTrees", "SecurityCam"]);

            return [
                {
                    label: "Teaching",
                    value: teaching.value,
                    sections: teaching.sections,
                },
                {
                    label: "Offices",
                    value: offices.value,
                    sections: offices.sections,
                },
                {
                    label: "Support",
                    value: support.value,
                    sections: support.sections,
                },
                {
                    label: "Amenities",
                    value: amenities.value,
                    sections: amenities.sections,
                },
                {
                    label: "Grounds & Security",
                    value: grounds.value,
                    sections: grounds.sections,
                },
            ];
        },
        topSectionsChartData() {
            return this.modules
                .filter(function (m) {
                    return (
                        m.enabled &&
                        m.key !== "BuildingOverview" &&
                        m.key !== "FacilityAreas" &&
                        m.count !== null &&
                        m.count > 0
                    );
                })
                .slice()
                .sort(function (a, b) {
                    return (b.count || 0) - (a.count || 0);
                })
                .slice(0, 8)
                .map(function (m) {
                    return { key: m.key, label: m.label, count: m.count || 0 };
                });
        },
        seatingCapacityChartData() {
            var s = this.summary;
            return [
                { key: "Classrooms", label: "Classrooms", value: s.classroom_seats || 0 },
                { key: "Labs", label: "Labs", value: s.lab_seats || 0 },
                { key: "Auditorium", label: "Auditoriums", value: s.auditorium_capacity || 0 },
                { key: "MeetingRooms", label: "Meeting Rooms", value: s.meeting_room_capacity || 0 },
            ];
        },
        breadCrumbItems() {
            var campusKey = this.campusConfig ? this.campusConfig.key : "";
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                {
                    label: this.campusTitle || "Campus",
                    path: campusKey ? "/adpoly/" + campusKey : "/adpoly",
                },
                {
                    label: "Campus",
                    path: campusKey ? "/adpoly/" + campusKey + "/campus" : "/adpoly",
                },
                { label: "Facility" },
            ];
        },
        activeModuleComponent() {
            if (!this.activeModule) {
                return null;
            }
            return resolveModuleComponent(this.activeModule.key);
        },
        activeModuleKey() {
            if (!this.activeModule) return "";
            return (
                this.activeModule.key +
                "-" +
                (this.activePlantTypeFilter ? this.activePlantTypeFilter : "all")
            );
        },
        palmTreesInitialPlantType() {
            if (!this.activeModule || this.activeModule.key !== "PalmAndTrees") {
                return "";
            }
            return this.activePlantTypeFilter || "";
        },
        activeModuleExtraProps() {
            if (!this.activeModule || this.activeModule.key !== "PalmAndTrees") {
                return {};
            }
            return {
                initialPlantType: this.activePlantTypeFilter || "",
            };
        },
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
    },
    methods: {
        pluralize(unit, count) {
            if (!unit) return "";
            if (count === 1) return unit;

            var lower = unit.toLowerCase();
            var last = lower.charAt(lower.length - 1);
            var secondLast = lower.length > 1 ? lower.charAt(lower.length - 2) : "";

            // Already plural-looking (ends with s) — leave as-is
            if (last === "s") return unit;

            // Words ending in consonant + y → ies (facility → facilities)
            if (last === "y" && "aeiou".indexOf(secondLast) === -1) {
                return unit.slice(0, -1) + "ies";
            }

            // Words ending in s, x, z, ch, sh → es
            if (
                last === "x" ||
                last === "z" ||
                lower.slice(-2) === "ch" ||
                lower.slice(-2) === "sh"
            ) {
                return unit + "es";
            }

            return unit + "s";
        },
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        validateCampus() {
            if (!isValidCampus(this.campus)) {
                this.$router.replace("/adpoly");
            }
        },
        openModule(module, options) {
            if (!module || !module.enabled) return;

            var permissionMap = {
                BuildingOverview: "facility-building-overview-index",
                FacilityAreas: "facility-areas-index",
                Classrooms: "facility-classrooms-index",
                Labs: "facility-labs-index",
                AcademicOffices: "facility-academic-offices-index",
                AdminOffices: "facility-admin-offices-index",
                Auditorium: "facility-auditoriums-index",
                MeetingRooms: "facility-meeting-rooms-index",
                CarParking: "facility-parking-index",
                Gates: "facility-gates-index",
                Toilets: "facility-toilets-index",
                Stores: "facility-stores-index",
                ServiceRooms: "facility-service-rooms-index",
                SportFacilities: "facility-sport-facilities-index",
                Canteen: "facility-canteens-index",
                Clinic: "facility-clinics-index",
                PrayerRoom: "facility-prayer-rooms-index",
                PantriesKitchen: "facility-pantries-index",
                VendingMachines: "facility-vending-machines-index",
                PalmAndTrees: "facility-palm-trees-index",
                SecurityCam: "facility-security-cameras-index",
            };
            var requiredPermission = permissionMap[module.key];

            if (requiredPermission && !this.hasPermission(requiredPermission)) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "warning",
                        title: "No access",
                        text: "You do not have permission to view this section.",
                        timer: 2500,
                        showConfirmButton: false,
                    });
                }
                return;
            }

            this.activePlantTypeFilter =
                options && options.plantType ? options.plantType : "";
            this.activeModule = module;
        },
        onTopSectionSelect(payload) {
            if (!payload || !payload.key) return;
            var module = this.modules.find(function (m) {
                return m.key === payload.key;
            });
            if (module) {
                this.openModule(module, {
                    plantType: payload.plantType || "",
                });
            }
        },
        onPalmTreesTypeSelect(payload) {
            if (!payload || !payload.plantType) return;
            var module = this.modules.find(function (m) {
                return m.key === "PalmAndTrees";
            });
            if (module) {
                this.openModule(module, {
                    plantType: payload.plantType,
                });
            }
        },
        closeModule() {
            this.activeModule = null;
            this.activePlantTypeFilter = "";
        },
        onModuleChanged() {
            this.loadSummary();
        },
        loadSummary() {
            var self = this;
            if (!this.campusCode) return;

            this.summaryLoading = true;
            this.facilityArea = null;
            this.$apollo
                .query({
                    query: SUMMARY_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var nextSummary = {
                        floors_count: 0,
                        classrooms: 0,
                        labs: 0,
                        offices: 0,
                        academic_offices: 0,
                        admin_offices: 0,
                        seating_capacity: 0,
                        classroom_seats: 0,
                        lab_seats: 0,
                        auditorium_capacity: 0,
                        meeting_room_capacity: 0,
                        parking: 0,
                        toilets: 0,
                        prayer_rooms: 0,
                        palm_trees_by_type: [],
                        security_cameras_by_area: [],
                    };

                    function setModuleCount(key, count) {
                        var mod = self.modules.find(function (m) {
                            return m.key === key;
                        });
                        if (mod) {
                            mod.count = count;
                        }
                    }

                    var areaPayload =
                        data && data.facilityArea ? data.facilityArea : null;
                    if (areaPayload && !areaPayload.error && areaPayload.data) {
                        self.facilityArea = areaPayload.data;
                    } else if (areaPayload && areaPayload.error) {
                        self.toastError(areaPayload.message || "Failed to load facility areas");
                    }

                    var areasSummaryPayload =
                        data && data.facilityAreasSummary
                            ? data.facilityAreasSummary
                            : null;
                    if (areasSummaryPayload && !areasSummaryPayload.error && areasSummaryPayload.data) {
                        setModuleCount("FacilityAreas", areasSummaryPayload.data.total || 0);
                    } else if (areasSummaryPayload && areasSummaryPayload.error) {
                        self.toastError(
                            areasSummaryPayload.message || "Failed to load campus areas summary"
                        );
                    }

                    var boPayload =
                        data && data.facilityBuildingOverviewsSummary
                            ? data.facilityBuildingOverviewsSummary
                            : null;
                    if (boPayload && !boPayload.error && boPayload.data) {
                        nextSummary.floors_count = boPayload.data.floors_count || 0;
                        setModuleCount("BuildingOverview", boPayload.data.total || 0);
                    } else if (boPayload && boPayload.error) {
                        self.toastError(boPayload.message || "Failed to load summary");
                    }

                    var crPayload =
                        data && data.facilityClassroomsSummary
                            ? data.facilityClassroomsSummary
                            : null;
                    if (crPayload && !crPayload.error && crPayload.data) {
                        nextSummary.classrooms = crPayload.data.total || 0;
                        nextSummary.classroom_seats = crPayload.data.total_seats || 0;
                        nextSummary.seating_capacity += nextSummary.classroom_seats;
                        setModuleCount("Classrooms", nextSummary.classrooms);
                    } else if (crPayload && crPayload.error) {
                        self.toastError(crPayload.message || "Failed to load classrooms summary");
                    }

                    var labPayload =
                        data && data.facilityLabsSummary
                            ? data.facilityLabsSummary
                            : null;
                    if (labPayload && !labPayload.error && labPayload.data) {
                        nextSummary.labs = labPayload.data.total || 0;
                        nextSummary.lab_seats = labPayload.data.total_seats || 0;
                        nextSummary.seating_capacity += nextSummary.lab_seats;
                        setModuleCount("Labs", nextSummary.labs);
                    } else if (labPayload && labPayload.error) {
                        self.toastError(labPayload.message || "Failed to load labs summary");
                    }

                    var aoPayload =
                        data && data.facilityAcademicOfficesSummary
                            ? data.facilityAcademicOfficesSummary
                            : null;
                    var academicOffices = 0;
                    if (aoPayload && !aoPayload.error && aoPayload.data) {
                        academicOffices = aoPayload.data.total || 0;
                        setModuleCount("AcademicOffices", academicOffices);
                    } else if (aoPayload && aoPayload.error) {
                        self.toastError(aoPayload.message || "Failed to load academic offices summary");
                    }

                    var adminPayload =
                        data && data.facilityAdminOfficesSummary
                            ? data.facilityAdminOfficesSummary
                            : null;
                    var adminOffices = 0;
                    if (adminPayload && !adminPayload.error && adminPayload.data) {
                        adminOffices = adminPayload.data.total || 0;
                        setModuleCount("AdminOffices", adminOffices);
                    } else if (adminPayload && adminPayload.error) {
                        self.toastError(adminPayload.message || "Failed to load admin offices summary");
                    }
                    nextSummary.academic_offices = academicOffices;
                    nextSummary.admin_offices = adminOffices;
                    nextSummary.offices = academicOffices + adminOffices;

                    var audPayload =
                        data && data.facilityAuditoriumsSummary
                            ? data.facilityAuditoriumsSummary
                            : null;
                    if (audPayload && !audPayload.error && audPayload.data) {
                        nextSummary.auditorium_capacity = audPayload.data.total_capacity || 0;
                        nextSummary.seating_capacity += nextSummary.auditorium_capacity;
                        setModuleCount("Auditorium", audPayload.data.total || 0);
                    } else if (audPayload && audPayload.error) {
                        self.toastError(audPayload.message || "Failed to load auditoriums summary");
                    }

                    var mrPayload =
                        data && data.facilityMeetingRoomsSummary
                            ? data.facilityMeetingRoomsSummary
                            : null;
                    if (mrPayload && !mrPayload.error && mrPayload.data) {
                        nextSummary.meeting_room_capacity = mrPayload.data.total_capacity || 0;
                        nextSummary.seating_capacity += nextSummary.meeting_room_capacity;
                        setModuleCount("MeetingRooms", mrPayload.data.total || 0);
                    } else if (mrPayload && mrPayload.error) {
                        self.toastError(mrPayload.message || "Failed to load meeting rooms summary");
                    }

                    var paPayload =
                        data && data.facilityParkingAreasSummary
                            ? data.facilityParkingAreasSummary
                            : null;
                    var ptPayload =
                        data && data.facilityParkingTransportServicesSummary
                            ? data.facilityParkingTransportServicesSummary
                            : null;
                    var phPayload =
                        data && data.facilityParkingHelipadsSummary
                            ? data.facilityParkingHelipadsSummary
                            : null;
                    var parkingTotal = 0;
                    if (paPayload && !paPayload.error && paPayload.data) {
                        parkingTotal += paPayload.data.total || 0;
                        nextSummary.parking = paPayload.data.total || 0;
                    } else if (paPayload && paPayload.error) {
                        self.toastError(paPayload.message || "Failed to load parking areas summary");
                    }
                    if (ptPayload && !ptPayload.error && ptPayload.data) {
                        parkingTotal += ptPayload.data.total || 0;
                    } else if (ptPayload && ptPayload.error) {
                        self.toastError(ptPayload.message || "Failed to load parking transport summary");
                    }
                    if (phPayload && !phPayload.error && phPayload.data) {
                        parkingTotal += phPayload.data.total || 0;
                    } else if (phPayload && phPayload.error) {
                        self.toastError(phPayload.message || "Failed to load parking helipad summary");
                    }
                    setModuleCount("CarParking", parkingTotal);

                    var gatesPayload =
                        data && data.facilityGatesSummary
                            ? data.facilityGatesSummary
                            : null;
                    if (gatesPayload && !gatesPayload.error && gatesPayload.data) {
                        setModuleCount("Gates", gatesPayload.data.total || 0);
                    } else if (gatesPayload && gatesPayload.error) {
                        self.toastError(gatesPayload.message || "Failed to load gates summary");
                    }

                    var toiletsPayload =
                        data && data.facilityToiletsSummary
                            ? data.facilityToiletsSummary
                            : null;
                    if (toiletsPayload && !toiletsPayload.error && toiletsPayload.data) {
                        nextSummary.toilets = toiletsPayload.data.total || 0;
                        setModuleCount("Toilets", nextSummary.toilets);
                    } else if (toiletsPayload && toiletsPayload.error) {
                        self.toastError(toiletsPayload.message || "Failed to load toilets summary");
                    }

                    var storesPayload =
                        data && data.facilityStoresSummary
                            ? data.facilityStoresSummary
                            : null;
                    if (storesPayload && !storesPayload.error && storesPayload.data) {
                        setModuleCount("Stores", storesPayload.data.total || 0);
                    } else if (storesPayload && storesPayload.error) {
                        self.toastError(storesPayload.message || "Failed to load stores summary");
                    }

                    var serviceRoomsPayload =
                        data && data.facilityServiceRoomsSummary
                            ? data.facilityServiceRoomsSummary
                            : null;
                    if (serviceRoomsPayload && !serviceRoomsPayload.error && serviceRoomsPayload.data) {
                        setModuleCount("ServiceRooms", serviceRoomsPayload.data.total || 0);
                    } else if (serviceRoomsPayload && serviceRoomsPayload.error) {
                        self.toastError(serviceRoomsPayload.message || "Failed to load service rooms summary");
                    }

                    var remainingSummaries = [
                        { key: "SportFacilities", root: "facilitySportFacilitiesSummary", label: "sport facilities" },
                        { key: "Canteen", root: "facilityCanteensSummary", label: "canteens" },
                        { key: "Clinic", root: "facilityClinicsSummary", label: "clinics" },
                        { key: "PrayerRoom", root: "facilityPrayerRoomsSummary", label: "prayer rooms" },
                        { key: "PantriesKitchen", root: "facilityPantriesSummary", label: "pantries" },
                        { key: "VendingMachines", root: "facilityVendingMachinesSummary", label: "vending machines" },
                        { key: "PalmAndTrees", root: "facilityPalmTreesSummary", label: "palm and trees" },
                        { key: "SecurityCam", root: "facilitySecurityCamerasSummary", label: "security cameras" },
                    ];
                    for (var ri = 0; ri < remainingSummaries.length; ri++) {
                        var meta = remainingSummaries[ri];
                        var remPayload = data && data[meta.root] ? data[meta.root] : null;
                        if (remPayload && !remPayload.error && remPayload.data) {
                            var remTotal = remPayload.data.total || 0;
                            setModuleCount(meta.key, remTotal);
                            if (meta.key === "PrayerRoom") {
                                nextSummary.prayer_rooms = remTotal;
                            }
                            if (meta.key === "PalmAndTrees") {
                                nextSummary.palm_trees_by_type =
                                    remPayload.data.by_plant_type || [];
                            }
                            if (meta.key === "SecurityCam") {
                                nextSummary.security_cameras_by_area =
                                    remPayload.data.by_area || [];
                            }
                        } else if (remPayload && remPayload.error) {
                            self.toastError(remPayload.message || ("Failed to load " + meta.label + " summary"));
                        }
                    }

                    self.summary = nextSummary;
                })
                .catch(function (err) {
                    var msg =
                        err && err.message
                            ? err.message
                            : "Failed to load facility summary";
                    self.toastError(msg);
                })
                .finally(function () {
                    self.summaryLoading = false;
                });
        },
        toastError(message) {
            if (!this.$swal) return;
            this.$swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: message,
                showConfirmButton: false,
                timer: 3500,
                timerProgressBar: true,
            });
        },
        formatAreaValue(value) {
            if (value === null || value === undefined) return "—";
            return Number(value).toLocaleString("en-US");
        },
        exportAllExcel: function () {
            var store = this.$store;
            var apolloClient = this.$apollo && this.$apollo.provider
                ? this.$apollo.provider.defaultClient
                : null;
            if (!apolloClient) return;

            var entity = this.entity;
            var campusCode = this.campusCode;
            var fileName = "ADPOLY_" + campusCode + "_Facility_All_Sections.xlsx";
            var totalSheets = getTotalSheetCount();

            store
                .dispatch("exportManager/addJob", {
                    fileName: fileName,
                    totalSheets: totalSheets,
                })
                .then(function (jobId) {
                    store.dispatch("exportManager/setMinimized", false);

                    exportAllFacilitySections(
                        apolloClient,
                        entity,
                        campusCode,
                        function (progress) {
                            store.dispatch("exportManager/updateJob", {
                                id: jobId,
                                progress: progress.percent,
                                currentSheet: progress.sheetName,
                                totalSheets: progress.totalSheets,
                            });
                        }
                    )
                        .then(function (result) {
                            store.dispatch("exportManager/completeJob", {
                                id: jobId,
                                rowCount: result.rowCount,
                            });
                        })
                        .catch(function (error) {
                            store.dispatch("exportManager/failJob", {
                                id: jobId,
                                error: (error && error.message) || "Export failed",
                            });
                        });
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-hero {
    background: linear-gradient(135deg, #fffbeb 0%, #ffffff 55%, #fef3c7 100%);
    border-color: #fde68a;
}

.fac-hero__eyebrow {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-hero__badge {
    display: inline-flex;
    flex-direction: column;
    padding: 0.65rem 0.9rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid #fde68a;
}

.fac-hero__badge-label {
    font-size: 0.65rem;
    font-weight: 600;
    color: #92400e;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.fac-hero__badge-value {
    font-size: 0.9rem;
    font-weight: 700;
    color: #1f2937;
}

.fac-charts__eyebrow {
    margin: 0;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-charts__heading {
    margin: 0.2rem 0 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1c1917;
}

.fac-kpi {
    position: relative;
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 1rem;
    padding: 1rem 1.15rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.fac-kpi--tip {
    cursor: help;
}

.fac-kpi--tip:hover,
.fac-kpi--tip:focus-within {
    border-color: #fbbf24;
    box-shadow: 0 4px 14px rgba(180, 83, 9, 0.1);
}

.fac-kpi__label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.fac-kpi__label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #78716c;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.fac-kpi__hint {
    flex-shrink: 0;
    color: #d6d3d1;
    font-size: 0.7rem;
    line-height: 1;
    transition: color 0.15s ease;
}

.fac-kpi--tip:hover .fac-kpi__hint,
.fac-kpi--tip:focus-within .fac-kpi__hint {
    color: #b45309;
}

.fac-kpi__value {
    margin-top: 0.35rem;
    font-size: 1.75rem;
    font-weight: 800;
    color: #1f2937;
    line-height: 1.1;
}

.fac-area-tile {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 1.05rem 1.1rem;
    border-radius: 1rem;
    border: 1px solid #e7e5e4;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.fac-area-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px -14px rgba(28, 25, 23, 0.28);
}

.fac-area-tile__icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
}

.fac-area-tile__label {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #78716c;
}

.fac-area-tile__value {
    margin: 0.3rem 0 0;
    font-size: 1.35rem;
    font-weight: 800;
    color: #1c1917;
    line-height: 1.15;
}

.fac-area-tile__unit {
    margin-left: 0.2rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #78716c;
}

.fac-area-tile--teal {
    border-color: #99f6e4;
    background: linear-gradient(160deg, #ffffff 0%, #f0fdfa 100%);
}
.fac-area-tile--teal .fac-area-tile__icon {
    background: #ccfbf1;
    color: #0f766e;
}

.fac-area-tile--amber {
    border-color: #fde68a;
    background: linear-gradient(160deg, #ffffff 0%, #fffbeb 100%);
}
.fac-area-tile--amber .fac-area-tile__icon {
    background: #fef3c7;
    color: #b45309;
}

.fac-area-tile--orange {
    border-color: #fdba74;
    background: linear-gradient(160deg, #ffffff 0%, #fff7ed 100%);
}
.fac-area-tile--orange .fac-area-tile__icon {
    background: #ffedd5;
    color: #c2410c;
}

.fac-area-tile--stone {
    border-color: #d6d3d1;
    background: linear-gradient(160deg, #ffffff 0%, #fafaf9 100%);
}
.fac-area-tile--stone .fac-area-tile__icon {
    background: #e7e5e4;
    color: #57534e;
}

.fac-area-tile--empty .fac-area-tile__value {
    color: #a8a29e;
    font-weight: 700;
}

.fac-kpi__tooltip {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 0.65rem);
    z-index: 20;
    width: max-content;
    max-width: 15.5rem;
    padding: 0.7rem 0.85rem;
    border-radius: 0.75rem;
    background: linear-gradient(160deg, #1c1917 0%, #292524 100%);
    color: #fafaf9;
    box-shadow: 0 12px 28px rgba(28, 25, 23, 0.28);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateX(-50%) translateY(4px);
    transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;
}

.fac-kpi__tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: #292524 transparent transparent transparent;
}

.fac-kpi--tip:hover .fac-kpi__tooltip,
.fac-kpi--tip:focus-within .fac-kpi__tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
}

.fac-kpi__tooltip-title {
    margin: 0 0 0.3rem;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #fbbf24;
}

.fac-kpi__tooltip-text {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 500;
    line-height: 1.45;
    color: #e7e5e4;
}

.fac-module {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    padding: 1rem;
    border-radius: 1rem;
    border: 1px solid #e7e5e4;
    background: #fafaf9;
    min-height: 7.5rem;
    transition: all 0.15s ease;
}

.fac-module--active {
    background: #fff;
    cursor: pointer;
}

.fac-module--active:hover {
    border-color: #f59e0b;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.12);
    transform: translateY(-1px);
}

.fac-module--soon {
    opacity: 0.65;
    cursor: not-allowed;
}

.fac-module__icon {
    font-size: 1.35rem;
    margin-bottom: 0.4rem;
}

.fac-module__label {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.25;
}

.fac-module__count {
    margin-top: auto;
    padding-top: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #b45309;
}

.fac-module__soon {
    margin-top: auto;
    padding-top: 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #a8a29e;
}

.fac-export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.45rem 0.85rem;
    border-radius: 0.65rem;
    border: 1px solid #f59e0b;
    background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%);
    color: #92400e;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.2;
    transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.fac-export-btn:hover:not(:disabled) {
    border-color: #d97706;
    background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
}

.fac-export-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
